import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { shop } from "@/api/shop";
import { useCart } from "@/cart/CartContext";
import { Price } from "@/components/Price";

export function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") ?? "";
  const isDev = params.get("dev") === "1";
  const { clear } = useCart();

  const { data: order } = useQuery({
    queryKey: ["shop", "order-by-session", sessionId],
    queryFn: () => shop.orderBySession(sessionId),
    enabled: !!sessionId,
    refetchInterval: (q) => (q.state.data?.status === "paid" ? false : 2000),
  });

  useEffect(() => {
    if (order?.status === "paid") clear();
  }, [order?.status, clear]);

  // Dev activation: entitlement already granted server-side, no Stripe session.
  if (isDev && !sessionId) {
    return (
      <div className="mx-auto max-w-lg">
        <div className="rounded-2xl border border-line bg-surface p-8 text-center shadow-card">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-brand-soft text-2xl">
            ⚡
          </div>
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-ink">Add-on activated</h1>
          <p className="mt-2 text-ink-soft">
            Activated without payment (testing). It's now unlocked on your account.
          </p>
          <div className="mt-5 flex justify-center gap-3">
            <Link to="/entitlements" className="rounded-lg bg-brand px-4 py-2 font-semibold text-white hover:bg-brand-strong">
              View my add-ons
            </Link>
            <Link to="/" className="rounded-lg border border-line px-4 py-2 font-medium text-ink-soft hover:text-ink hover:border-line-strong">
              Keep shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="mx-auto max-w-lg text-center">
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Thanks!</h1>
        <Link to="/" className="mt-3 inline-block font-medium text-brand-strong">
          Back to catalog →
        </Link>
      </div>
    );
  }

  const paid = order?.status === "paid";

  return (
    <div className="mx-auto max-w-lg">
      <div className="rounded-2xl border border-line bg-surface p-8 text-center shadow-card">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-brand-soft text-2xl">
          {paid ? "🎉" : "⏳"}
        </div>
        <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-ink">
          {paid ? "Payment complete" : "Confirming payment…"}
        </h1>
        <p className="mt-2 text-ink-soft">
          {paid
            ? "Your add-ons are now active on your account."
            : "Hang tight — we're confirming your payment with Stripe."}
        </p>
      </div>

      {order && (
        <div className="mt-4 rounded-xl border border-line bg-surface p-5 shadow-card">
          <div className="mb-2 text-sm text-ink-mute">Order {order.id.slice(0, 8)}</div>
          {order.items.map((i) => (
            <div key={i.sku_code} className="flex justify-between text-sm text-ink-soft">
              <span>
                {i.product_name} <span className="text-ink-mute">× {i.quantity}</span>
              </span>
              <Price cents={i.unit_price_cents * i.quantity} currency={order.currency} />
            </div>
          ))}
          <div className="mt-2 flex justify-between border-t border-line pt-2 font-bold text-ink">
            <span>Total</span>
            <Price cents={order.total_cents} currency={order.currency} />
          </div>
        </div>
      )}

      <div className="mt-5 flex justify-center gap-3">
        <Link
          to="/entitlements"
          className="rounded-lg bg-brand px-4 py-2 font-semibold text-white hover:bg-brand-strong"
        >
          View my add-ons
        </Link>
        <Link
          to="/"
          className="rounded-lg border border-line px-4 py-2 font-medium text-ink-soft hover:text-ink hover:border-line-strong"
        >
          Keep shopping
        </Link>
      </div>
    </div>
  );
}
